import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Dimensions,
  TextInput,
  FlatList,
  Platform
  } from 'react-native';
import countries from "i18n-iso-countries";
import { Flag } from "./Flag";
import { useApiWithLoading } from '../hooks/useApiWithLoading';
import {
  useSessionStore,
  consumePendingFlowCloseResetIfNeeded,
} from "../../../state/src/useSessionStore";
import type { SessionState } from "../../../state/src/useSessionStore";
import { i18n } from "../i18n/i18n-setup";
import { useTranslation } from "../i18n/useTranslation";
import { loadCountries, getLocationDetailsByKID, postFeeLookup } from '../services/common.service';
import { ProductType } from '../view-models/productType';
import { scale, moderateScale, verticalScale } from '../constants/scaling';
import Colors from '../constants/colors';
import GradientBackground from './uiComponents/GradientBackground';
import LocationBarGradientBackground from './uiComponents/LocationBarGradientBackground';
import PickupIcon from "../../../../assets/images/pickupIcon.svg";
import LocationIcon from "../../../../assets/images/locationB.svg";
import BillPayIcon from "../../../../assets/images/bill.svg";
import SendIcon from "../../../../assets/images/send.svg";
import CheckIcon from "../../../../assets/images/check.svg";
import CVSLogo from "../../../../assets/images/cvs-logo-2.svg";
import UpDownIcon from "../../../../assets/images/arr.chevron.up.down.svg";
import EnglishFlag from "../../../../assets/images/EnglishFlag.svg";
import SpanishFlag from "../../../../assets/images/SpanishFlag.svg";
import MoneygramLogo from "../../../../assets/images/logo-moneygram-color.svg";
import { getPartnerHierarchy } from '../services/common.service';
import { useGlobalError } from './uiComponents/GlobalErrorProvider';
import enLocale from "i18n-iso-countries/langs/en.json";
import { capturePosthogEvent } from '../utils/analytics';

countries.registerLocale(enLocale);

const { width } = Dimensions.get('window');

type DashboardLanguage = 'english' | 'spanish';

interface DashboardProps {
  onLanguageChange?: (language: DashboardLanguage) => void;
  onServicePress?: (service: 'pickup' | 'send' | 'pay') => void;
  onGetStarted?: () => void;
}

type Country = {
  id: number;
  CountryCode: string;
  CountryName: string;
  IsActive: boolean;
};

export const Dashboard: React.FC<DashboardProps> = ({
  onLanguageChange,
  onServicePress,
  onGetStarted,
}) => {
  const { executeWithLoading } = useApiWithLoading();
  const isLoading = useSessionStore((s: SessionState) => (s as any).isLoading);
  const [selectedLanguage, setSelectedLanguageLocal] = useState<DashboardLanguage>('english');
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>('');
  const [sendAmount, setSendAmount] = useState('100');
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { showError } = useGlobalError();
  const [newCountries, setCountries] = useState<Array<{
    toLowerCase: any; id: number; CountryCode: string; CountryName: string; IsActive: boolean 
  }>>([]);
  
  const [isCountriesLoading, setIsCountriesLoading] = useState<boolean>(false);
  const [countriesError, setCountriesError] = useState<string | null>(null);
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState<boolean>(false);
  const [isFeeLookupLoading, setIsFeeLookupLoading] = useState<boolean>(false);
  const [kid, setKid] = useState<string | null>(null);
  const { t } = useTranslation();
  const [locationDetails, setLocationDetails] = useState<{
    AgentId?: string | null;
    UnitProfileId?: string | null;
    KioskUnitId?: string | null;
    LocationName?: string | null;
  } | null>(null);
  const lastFeeLookupKeyRef = useRef<string | null>(null);

  // After header X: navigate to `/` first, then clear flow state on this mount (avoids step URL race).
  useEffect(() => {
    consumePendingFlowCloseResetIfNeeded();
  }, []);

  // Partner hierarchy details
  const [partnerDetails, setPartnerDetails] = useState<{ name: string; address: string } | null>(null);

  const hasText = (v: any) => typeof v === 'string' && v.trim().length > 0;
  const isStoreReady = hasText(partnerDetails?.name) && hasText(partnerDetails?.address);

  // Store Required Data into zustand
  const setIsReceiver = useSessionStore((s: SessionState) => (s as any).setIsReceiver);
  const setIsSendCash = useSessionStore((s: SessionState) => (s as any).setIsSendCash);
  const setIsBillPay = useSessionStore((s: SessionState) => (s as any).setIsBillPay);

  const setAgentId = useSessionStore((s: SessionState) => s.setAgentId);
  const setUnitProfileId = useSessionStore((s: SessionState) => s.setUnitProfileId);
  const setselectedLanguage = useSessionStore((s: SessionState) => s.setselectedLanguage);
  const setKioskId = useSessionStore((s: SessionState) => (s as any).setKioskId);
  const setQrCodeNumber = useSessionStore((s: SessionState) => (s as any).setQrCodeNumber);
  const setLocationNameStore = useSessionStore((s: SessionState) => (s as any).setLocationName);
  const setPartnerDetailsStore = useSessionStore((s: SessionState) => (s as any).setPartnerDetails);
  // const navigation = useNavigation<any>();

  // Add state for exchange rate information
  const [exchangeRateInfo, setExchangeRateInfo] = useState<{
    exchangeRate: string;
    receiveCurrency: string;
    isEstimated: boolean;
    totalSendFees: number;
  } | null>(null);

  // Dynamic flag mapping - you can expand this as needed
  const getFlagForCode = (code: string): string => {
    const flagMap: Record<string, string> = {
      MEX: '🇲🇽',
      PHL: '🇵🇭',
      JAM: '🇯🇲',
      ARM: '🇦🇲',
      IND: '🇮🇳',
    };
    return flagMap[code] || '🌐';
  };

  // filter countries (fetched from API)
  const filteredCountries: Country[] = useMemo(() => {
    const list = (newCountries as Country[]).filter((c) => !!c);
    const normalizedQuery = search.toLowerCase().trim();
    const toName = (c: Country) => (typeof c?.CountryName === 'string' ? c.CountryName : '').trim();
    const base = normalizedQuery
      ? list.filter((c) => toName(c).toLowerCase().includes(normalizedQuery))
      : [...list].sort((a, b) => toName(a).localeCompare(toName(b)));
    return base.slice(0, 5);
  }, [countries, search]);

  // Fetch countries only after we have a valid kiosk token (kid)
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        await executeWithLoading(async () => {
          const result = await loadCountries();

          // The error is because the state expects objects with a 'toLowerCase' property, but Country does not have it.
          // To fix, add a dummy toLowerCase property to each country object.
          const apiCountries: Country[] = result?.data ?? [];
          setCountries(
            apiCountries.map((c) => ({
              ...c,
              toLowerCase: undefined, // or you can use: () => c.CountryName?.toLowerCase?.() ?? ''
            }))
          );

          if (apiCountries.length > 0) {
            const defaultCountry = apiCountries.find(
              (country) => country.CountryCode === "MEX"
            );

            if (defaultCountry) {
              setSelectedCountry(defaultCountry.CountryName);
              setSelectedCountryCode(defaultCountry.CountryCode);
            } else {
              // fallback: set first country if India is not found
              setSelectedCountry(apiCountries[0].CountryName);
              setSelectedCountryCode(apiCountries[0].CountryCode);
            }
          }
        }, "Loading countries...");
      } catch (error) {
        console.error('💥 Failed to fetch countries:', error);
        setCountriesError('Failed to load countries');
      }
    };

    if (!kid) {
      return;
    }

    fetchCountries();
  }, [kid]);

// Fetch location details when KID is available
  useEffect(() => {
    const fetchLocationDetails = async () => {
      if (kid) {
        try {
          await executeWithLoading(async () => {
            //const result = await getLocationDetailsByKID(kid); 
            const result = {data: {AgentId: "44378361", UnitProfileId: "4345427", Status: "active"}};            // const result = {data: {AgentId: "66024839", UnitProfileId: "2668580", Status: "active"}};//await getLocationDetailsByKID(kid);
            if(result?.data?.Status?.toLowerCase() != 'active') {
              showError('Location status is ' + result?.data?.Status);
              return;
            }
            if (result?.data) {
              setLocationDetails(result.data);

              if (result.data.AgentId) {
                await fetchPartnerHierarchy(result.data.AgentId);
              }

              // if (selectedCountryCode && selectedCountry) {
              //   performFeeLookup(selectedCountryCode, selectedCountry);
              // }
            } else {
              setLocationDetails(null);
            }
          }, "Loading location...");
        } catch (error) {
          console.error('💥 Failed to fetch location details for KID:', error);
          setLocationDetails(null);
        }
      }
    };

    fetchLocationDetails();
  }, [kid, selectedCountryCode, selectedCountry]);

  // Persist agent + unit for send/receive flows: survives reload on /send/... without losing QR context.
  useEffect(() => {
    const aid = locationDetails?.AgentId;
    const uid = locationDetails?.UnitProfileId;
    if (aid && uid) {
      try {
        setAgentId(String(aid));
        setUnitProfileId(String(uid));
      } catch {
        /* ignore */
      }
    }
  }, [locationDetails?.AgentId, locationDetails?.UnitProfileId, setAgentId, setUnitProfileId]);

  const fetchPartnerHierarchy = async (agentId: string) => {
    try {
      await executeWithLoading(async () => {
        const payload = await getPartnerHierarchy(agentId);
        const name: string = (payload as any)?.name ?? '';
        const address: string = (payload as any)?.address ?? '';
        const city: string = (payload as any)?.city ?? '';
        const country: string = (payload as any)?.country ?? '';
        const state: string = (payload as any)?.state ?? '';
        const postalCode: string = (payload as any)?.postalCode ?? '';
        setPartnerDetails({ name, address });
        setLocationDetails((prev) => ({ ...(prev || {}), LocationName: name }));
        try { setLocationNameStore?.(name); } catch {}
        try { setPartnerDetailsStore?.({ name, address, city, country, state, postalCode }); } catch {}
      }, "Loading partner details...");
    } catch (error) {
      console.error('Failed to fetch PartnerHierarchy:', error);
    }
  };

  const handleSelect = (country: Country) => {
    setSelectedCountry(country.CountryName);
    setSelectedCountryCode(country.CountryCode);
    setIsCountryDropdownOpen(false);
    // Perform fee lookup when country is selected
    performFeeLookup(country.CountryCode, country.CountryName);
    // setSelectedCountry(country);
    setDropdownOpen(false);
    setSearch("");
  };

  const handleLanguageChange = (language: DashboardLanguage) => {
    if (language === 'english') {
      i18n.activate("en");
    } else {
      i18n.activate("es");
    }
    setSelectedLanguageLocal(language);
    try {
      setselectedLanguage?.(language);
    } catch {
      /* ignore */
    }
    onLanguageChange?.(language);
  };

  const handleServicePress = (service: 'pickup' | 'send' | 'pay') => {
    // Prevent interactions during initial/ongoing API calls
    if (isLoading || !isStoreReady) {
      return;
    }
    if(service === 'pickup') {
      try {
        setIsReceiver?.(true);
        setIsSendCash?.(false);
        setIsBillPay?.(false); 
      } catch {}
      capturePosthogEvent('Receive Flow - Initiated',{});
    } else if(service === 'send') {
      try {
        setIsReceiver?.(false);
        setIsSendCash?.(true);
        setIsBillPay?.(false);  } catch {}
        capturePosthogEvent('Send Flow - Initiated',{});
    } else if (service === 'pay') {
      try {
        setIsReceiver?.(false);
        setIsSendCash?.(false);
        setIsBillPay?.(true);  } catch {}
        capturePosthogEvent('Bill Pay Flow - Initiated',{});
    }
    onServicePress?.(service);
    setAgentId(locationDetails?.AgentId ?? null);
    setUnitProfileId(locationDetails?.UnitProfileId ?? null);
    // Still call optional prop callback
    onServicePress?.(service);
  };

  // const handleGetStarted = () => {
  //   onGetStarted?.();
  // };

  const performFeeLookup = async (countryCode: string, countryName: string) => {
    await executeWithLoading(async () => {
      if (!locationDetails?.AgentId || !locationDetails?.UnitProfileId) {
        console.log('Cannot perform fee lookup: Missing AgentId or UnitProfileId');
        return;
      }

      const feeLookupRequest = {
        agentID: locationDetails.AgentId,
        unitProfileID: locationDetails.UnitProfileId,
        amount: parseFloat(sendAmount.replace(/,/g, '')),
        sendCurrency: 'USD',
        destinationCountry: countryCode,
        allOptions: true,
        productType: ProductType.SEND,
        amountExcludingFee: 1,
      };

      const result = await postFeeLookup(feeLookupRequest);

      if (result?.exchangeRates?.length > 0) {
        const firstExchangeRate = result.exchangeRates[0];
        setExchangeRateInfo({
          exchangeRate: firstExchangeRate.exchangeRate?.toString() || '0',
          receiveCurrency: firstExchangeRate.receiveCurrency || '',
          isEstimated: firstExchangeRate.isEstimated || false,
          totalSendFees: firstExchangeRate.totalSendFees || 0,
        });
      } else {
        setExchangeRateInfo(null);
      }
    }, "Fetching rates & fees...");
  };

  // Read KID from URL query parameters on mount
  useEffect(() => {
    const readKidFromUrl = () => {
      try {
        // Check if we're in a browser environment
        if (typeof window !== 'undefined') {
          const urlParams = new URLSearchParams(window.location.search);
          const kidParam = urlParams.get('t');
          const session = useSessionStore.getState() as any;
          const fallbackKid = (session?.kioskId || session?.qrCodeNumber || '').toString().trim();
          const effectiveKid = kidParam || fallbackKid;
          if (effectiveKid) {
            setKid(effectiveKid);
            try { 
              setKioskId?.(effectiveKid);
              setQrCodeNumber?.(effectiveKid); // Store the QR code number separately
            } catch {}
          } else {
            showError(t('QR_DETAILS_MISSING'));
          }
        }
      } catch (error) {
        console.error('💥 Error reading KID from URL:', error);
      }
    };

    readKidFromUrl();
  }, []);

  useEffect(() => {
    const agentId = locationDetails?.AgentId;
    const unitId = locationDetails?.UnitProfileId;
    if (selectedCountryCode && selectedCountry && agentId && unitId) {
      const key = `${agentId}|${unitId}|${selectedCountryCode}|${selectedCountry}`;
      if (lastFeeLookupKeyRef.current === key) {
        return; // prevent duplicate calls (StrictMode/effect refires or unrelated location updates)
      }
      lastFeeLookupKeyRef.current = key;
      performFeeLookup(selectedCountryCode, selectedCountry);
    }
  }, [selectedCountryCode, selectedCountry, locationDetails?.AgentId, locationDetails?.UnitProfileId]);

  const parseAmount = (text: string): string => {
    // allow only digits and one optional decimal point
    const cleaned = text.replace(/[^0-9.]/g, "");
    const parts = cleaned.split(".");
    if (parts.length > 2) {
      return `${parts[0]}.${parts.slice(1).join("")}`;
    }
    return cleaned;
  };

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false} contentContainerStyle={styles.contentContainer}>
      {/* <View style={styles.locationInfo}>
        <View style={{ flexDirection: 'row' }}>
            <LocationIcon width={24} height={24}/>
          <Text numberOfLines={1} ellipsizeMode="tail" style={styles.locationInfoText} testID='partnerAddress'>
            {partnerDetails?.address && partnerDetails.address.length > 20
              ? partnerDetails.address.slice(0, 20) + '...'
              : partnerDetails?.address || 'N/A'}
          </Text>
        </View>
        <Text style={styles.locationAddressText} testID='partnerName'>{partnerDetails?.name || 'N/A'}</Text>
      </View> */}
      <LocationBarGradientBackground>
        <View style={styles.locationInfo}>
          <LocationIcon width={16} height={16}/>
          <View style={{ marginLeft: 8}}>
          <Text style={styles.locationInfoText} testID='partnerName'>{partnerDetails?.name || 'N/A'}</Text>
            <Text numberOfLines={1} ellipsizeMode="tail" style={styles.locationAddressText} testID='partnerAddress'>
              {/* {partnerDetails?.address && partnerDetails.address.length > 20
                ? partnerDetails.address.slice(0, 20) + '...'
                : partnerDetails?.address || 'N/A'} */}
                {partnerDetails?.address || 'N/A'}
            </Text>
          </View>
        </View>
      </LocationBarGradientBackground>
      
      {/* Language Selection */}
      <View style={styles.languageSection}>
        <Text style={styles.languageTitle}>{t("SELECT_LANGUAGE")}</Text>
        <View style={styles.languageButtons}>
          <TouchableOpacity
            testID="englishLanguageButton"
            style={[
              styles.languageButton,
              selectedLanguage === 'english' && styles.languageButtonSelected,
              { paddingRight: 4},
            ]}
            onPress={() => handleLanguageChange('english')}
          >
            <Flag iso3={'USA'} width={28} height={28} />
            <Text style={[
              styles.languageText,
              selectedLanguage === 'english' && styles.languageTextSelected,
            ]}>
              {t("ENGLISH")}
            </Text>
            {selectedLanguage === 'english' && (
            <CheckIcon width={16} height={16} />
            )}
          </TouchableOpacity>

          <TouchableOpacity
            testID="spanishLanguageButton"
            style={[
              styles.languageButton,
              selectedLanguage === 'spanish' && styles.languageButtonSelected,
              { paddingRight: 4},
            ]}
            onPress={() => handleLanguageChange('spanish')}
          >
            <Flag iso3={'MEX'} width={28} height={28} />
            <Text style={[
              styles.languageText,
              selectedLanguage === 'spanish' && styles.languageTextSelected,
            ]}>
              {t("SPANISH")}
            </Text>
            {selectedLanguage === 'spanish' && (
                <CheckIcon width={16} height={16} />
            )}
          </TouchableOpacity>
        </View>
      </View>
      <View style={styles.brandSection}>
        <CVSLogo width={160} height={40}/>
        <Text style={styles.slogan}>{t('SLOGAN')}</Text>
      </View>

      <View style={styles.servicesSection}>
        <TouchableOpacity
          testID="pickupCashButton"
          style={[styles.serviceButton, (isLoading || !kid || !isStoreReady) ? styles.disabledButton : undefined]}
          disabled={isLoading || !kid || !isStoreReady}
          onPress={() => handleServicePress('pickup')}
        >
          <View style={styles.serviceButtonInner}>
            <PickupIcon width={24} height={24}/>
            <Text style={styles.serviceText}>
              <Text style={styles.serviceTextLine} numberOfLines={1}>
                {t('PICK_UP')}
              </Text>
              {'\n'}
              <Text style={styles.serviceTextLine} numberOfLines={1}>
                {t('CASH')}
              </Text>
            </Text>
          </View>
        </TouchableOpacity>
        <TouchableOpacity
          testID="sendCashButton"
          style={[styles.serviceButton, (isLoading || !kid || !isStoreReady) ? styles.disabledButton : undefined]}
          disabled={isLoading || !kid || !isStoreReady}
          onPress={() => handleServicePress('send')}
        >
          <SendIcon width={24} height={24}/>
          <Text style={styles.serviceText}>
            <Text style={styles.serviceTextLine} numberOfLines={1}>
              {t('SEND')}
            </Text>
            {'\n'}
            <Text style={styles.serviceTextLine} numberOfLines={1}>
              {t('CASH')}
            </Text>
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          testID="billPayButton"
          style={[styles.serviceButton, (isLoading || !kid || !isStoreReady) ? styles.disabledButton : undefined]}
          disabled={isLoading || !kid || !isStoreReady}
          onPress={() => handleServicePress('pay')}
        >
          <BillPayIcon width={24} height={24}/>
          <Text style={styles.serviceText}>
            <Text style={styles.serviceTextLine} numberOfLines={1}>
              {t('PAY')}
            </Text>
            {'\n'}
            <Text style={styles.serviceTextLine} numberOfLines={1}>
              {t('BILLS')}
            </Text>
          </Text>
        </TouchableOpacity>
      </View>
      <View style={styles.ratesSection}>
        <Text style={styles.ratesTitle}>{t('EXPLORE_RATE_AND_FEES')}</Text>
        <View style={styles.inputGroup}>
          <Text style={styles.inputLabel}>{t('YOU_ARE_SENDING_TO')}</Text>
          <View style={styles.countrySelectWrapper}>
            <TouchableOpacity testID="countrySelector"
              style={[styles.countrySelector, (!isStoreReady || isLoading || !kid) ? styles.disabledButton : undefined]}
              disabled={!isStoreReady || isLoading || !kid}
              onPress={() => setDropdownOpen((prev) => !prev)}
            >
              <Flag iso3={selectedCountryCode ?? ''} width={40} height={40} />
              <Text style={styles.countryText}>{selectedCountry}</Text>
              <UpDownIcon width={24} height={24}/>
            </TouchableOpacity>
            <View style={styles.countryContainer}>
              {isDropdownOpen && (
              <View style={styles.dropdownOpen}>
                <TextInput
                  testID="countrySearchInput"
                  style={styles.searchInput}
                  placeholder="Search country here..."
                  value={search}
                  onChangeText={setSearch}
                />
                <FlatList
                  data={filteredCountries}
                  keyExtractor={(item) => item?.CountryCode || String(item?.id) || item?.CountryName}
                  renderItem={({ item }) => (
                    <TouchableOpacity
                      testID={`countrySelected`}
                      style={styles.countryItem}
                      onPress={() => handleSelect(item)}
                    >
                      <Text style={styles.countryText}>{item.CountryName}</Text>
                    </TouchableOpacity>
                  )}
                />
              </View>
              )}
            </View>
          </View>
          <Text style={styles.exchangeRate}>
            {exchangeRateInfo ? 
              `1 USD = ${exchangeRateInfo.exchangeRate} ${exchangeRateInfo.receiveCurrency}` : ''
            }
          </Text>
          {isFeeLookupLoading && (
            <Text style={styles.helperText}>🔄 {t('FETCHING_FEES')}</Text>
          )}
          {isCountriesLoading && (
            <Text style={styles.helperText}>{t('LOADING_COUNTRIES')}</Text>
          )}
          {!!countriesError && (
            <Text style={styles.errorText}>{countriesError}</Text>
          )}
        </View>
        <View style={styles.inputGroup}>
          <Text style={styles.inputLabel}>{t('YOU_SEND')}</Text>
          <View style={styles.amountInput}>
            <TextInput testID="sendAmount"
              style={styles.amountText}
              keyboardType="decimal-pad"
              placeholder="0.00"
              value={sendAmount}
              onChangeText={(t) => {
                const next = parseAmount(t);
                setSendAmount(next);
              }}
            />
            <Text style={styles.currencyText}>USD</Text>
          </View>
        </View>
        <View style={styles.inputGroup}>
          <Text style={styles.inputLabel}>{t('THEY_RECEIVE')}</Text>
          <View style={[styles.amountInput]}>
            <Text style={[styles.amountText, styles.amountTextGreen]} testID="receivedAmount">
              {exchangeRateInfo ? 
                (() => {
                  const numericAmount = parseFloat(sendAmount.replace(/,/g, ''));
                  const received = numericAmount * parseFloat(exchangeRateInfo.exchangeRate);
                  return `${received.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                })() : 
                '0.00'
              }
            </Text>
            <Text style={[styles.currencyText, { color: Colors.black}]}>
              {exchangeRateInfo?.receiveCurrency || ''}
            </Text>
          </View>
        </View>      
      </View>
      <View style={{ marginHorizontal: 24}}>
        <GradientBackground style={styles.card}>
          <View style={styles.row}>
            <Text style={styles.feeLabel}>{t("FEES")}</Text>
            <View style={{ flexDirection: 'row'}}>
              <Text style={styles.feeLabel}>{exchangeRateInfo?.totalSendFees || 0} USD</Text>
            </View>
          </View>
          <TouchableOpacity
            testID="getStartedButton"
            style={[styles.button, (isLoading || !kid || !isStoreReady) ? styles.disabledButton : undefined]}
            disabled={isLoading || !kid || !isStoreReady}
            onPress={() => handleServicePress('send')}
          >
            <Text style={styles.buttonText}>{t("GET_STARTED")}</Text>
          </TouchableOpacity>
        </GradientBackground>
      </View>
      <View style={styles.moneygramLogo}>
        <Text style={styles.poweredBy}>{t("POWERED_BY")}</Text>
        <MoneygramLogo width={70} height={16}/>
      </View>
    </ScrollView>
  );
};

export default Dashboard;

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    paddingBottom: 50,
  },
  locationInfo: {
    height: 60,
    width: '100%',
    paddingLeft: scale(12),
    paddingRight: scale(24),
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  locationInfoText: {
    fontFamily: 'Rubik',
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 22,
    color: Colors.secondaryText,
  },
  locationAddressText: {
    fontFamily: 'Rubik',
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 16,
    letterSpacing: 0.06,
    color: Colors.secondaryText,
  },
  languageSection: {
    marginHorizontal: 24,
    marginTop: 30,
  },
  languageTitle: {
    color: Colors.secondaryText,
    textAlign: 'center',
    fontFamily: 'Rubik',
    fontSize: 16,
    fontWeight: '400',
    lineHeight: verticalScale(20),
    letterSpacing: -0.16,
  },
  languageButtons: {
    flexDirection: 'row',
    justifyContent: "space-evenly",
    gap: 10,
    borderWidth: 1,
    borderColor: Colors.borderGrey,
    borderRadius: 8,
    padding: 4,
    marginTop: 12,
    alignSelf: 'stretch',
    minHeight: 48,
  },
  languageButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Colors.white,
    paddingVertical: 8,
    borderRadius: 8,
    gap: 8,
    justifyContent: 'center',
    paddingLeft: 16
  },
  languageButtonSelected: {
    backgroundColor: Colors.titleTextColor,
    borderWidth: 0,
  },
  flagContainer: {
    marginRight: 10,
    height: 28,
    width: 28,
    borderRadius: 50,
    overflow: "hidden",
    alignItems: "center",
    justifyContent: "center",
  },
  flag: {
    fontSize: 20,
  },
  languageText: {
    color: '#1F1F1F',
    textAlign: 'center',
    fontFamily: 'Rubik',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: verticalScale(22),
    letterSpacing: 0.07,
    marginRight: 8
  },
  languageTextSelected: {
    color: '#fff',
  },
  checkmark: {
    fontSize: 18,
    color: '#4CAF50',
    marginLeft: 8,
  },
  brandSection: {
    alignItems: 'center',
    marginTop: 60
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 32,
  },
  heartIcon: {
    fontSize: 32,
    marginRight: 8,
  },
  cvsText: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#e31837',
  },
  slogan: {
    fontFamily: 'Rubik',
    fontSize: 32,
    fontWeight: '500',
    color: '#1f1f1f',
    textAlign: 'center',
    lineHeight: 36,
    width: 330,
    // paddingHorizontal: 20,
    marginTop: 32
  },
  servicesSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'stretch',
    gap: 10,
    marginVertical: 40,
    marginHorizontal: 24,
  },
  serviceButton: {
    flex: 1,
    minWidth: 0,
    backgroundColor: '#F2F2F2',
    borderRadius: 20,
    paddingVertical: 16,
    paddingHorizontal: 10,
    alignItems: 'flex-start',
    justifyContent: 'space-evenly',
    borderColor:'#CACACA',
    borderWidth: 1,
     // iOS shadow
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    // Android shadow
    elevation: 8,     
  },
  disabledButton: {
    opacity: 0.5,
  },
  serviceButtonInner: {
    alignSelf: 'stretch',
    width: '100%',
    alignItems: 'flex-start',
  },
  serviceIcon: {
    height: 24,
    width: 24,
    marginBottom: 8,
  },
  serviceText: {
    fontFamily: 'Rubik',
    fontSize: 16,
    fontWeight: '400',
    color: Colors.secondaryText,
    width: '100%',
    minWidth: 0,
    textAlign: 'left',
    lineHeight: 24,
    alignSelf: 'stretch',
    marginTop: 8,
  },
  /** One line of a two-line service label — avoids iOS splitting a word (e.g. "Send") when the column is narrow. */
  serviceTextLine: {
    fontFamily: 'Rubik',
    fontSize: 16,
    fontWeight: '400',
    color: Colors.secondaryText,
    textAlign: 'left',
  },
  ratesSection: {
    backgroundColor: '#fff',
    marginHorizontal: 24,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    padding: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 3,
  },
  ratesTitle: {
    fontFamily: 'Rubik',
    fontSize: 24,
    fontWeight: '500',
    color: '#1F1F1F',
    marginBottom: 24,
    // textAlign: 'center',
  },
  inputGroup: {
    marginBottom: 24,
  },
  inputLabel: {
    fontFamily: 'Rubik',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 20,
    color: '#333',
    marginBottom: 4,
  },
  countrySelector: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    padding: 16,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#E8E8E8',
    marginBottom: 24,
  },
  countrySelectWrapper: {
    position: 'relative',
  },
  countryText: {
    fontSize: 20,
    fontFamily: 'Rubik',
    fontWeight: '500',
    color: '#333',
    lineHeight: 28,
    marginLeft: 16,
    flex: 1,
  },
  flagOverflow: {
    width: 40,
    height: 40,
    borderRadius: 50,
    overflow: "hidden",
    alignItems: "center",
    justifyContent: "center",
  },
  helperText: {
    marginTop: 6,
    fontSize: 12,
    color: '#666',
  },
  errorText: {
    marginTop: 6,
    fontSize: 12,
    color: '#d32f2f',
  },
  exchangeRate: {
    fontFamily: 'Rubik',
    fontSize: 16,
    color: Colors.successInfoGreen,
    fontWeight: '400',
    alignSelf: 'center',
    lineHeight: 16
  },
  amountInput: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    paddingHorizontal: 12,
    paddingVertical: 16,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#E8E8E8',
  },
  amountText: {
    fontFamily: 'Rubik',
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    flex: 1,
  },
  amountTextGreen: {
    color: '#008A00',
  },
  currencyText: {
    fontSize: 14,
    fontWeight: '600',
    fontFamily: 'Rubik',
    color: Colors.darkGrey,
    alignSelf: 'center',
    lineHeight: 20
  },
  readOnlyInput: {
    backgroundColor: '#fafafa',
  },
  previousAmount: {
    fontSize: 14,
    color: '#999',
    marginBottom: 4,
  },
  receivedAmountContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  receivedAmount: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#4CAF50',
    flex: 1,
  },
  receivedCurrency: {
    fontSize: 16,
    color: '#666',
  },
  feesContainer: {
    borderRadius: 12,
    padding: 15,
    marginBottom: 20,
    backgroundColor: '#FFE4B5',
  },
  feesRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  feesLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: '#333',
  },
  feesAmount: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  originalFee: {
    fontSize: 14,
    color: '#666',
    textDecorationLine: 'line-through',
  },
  freeText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#4CAF50',
  },
  getStartedButton: {
    backgroundColor: '#333',
    borderRadius: 12,
    padding: 18,
    alignItems: 'center',
  },
  getStartedText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '600',
  },
  footer: {
    alignItems: 'center',
    paddingVertical: 30,
    paddingHorizontal: 20,
  },
  poweredBy: {
    fontFamily: 'Rubik',
    fontSize: 12,
    fontWeight: '300',
    color: Colors.black,
    marginRight: 8
  },
  moneygramLogo: {
    flexDirection: 'row',
    alignContent: 'center',
    justifyContent: 'center',
    marginVertical: 30
  },
  text: {
    fontSize: 16,
  },
    countryContainer: {
  },
  dropdownOpen: {
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 8,
    backgroundColor: "#fff",
    maxHeight: 250,
  },
  searchInput: {
    borderBottomWidth: 1,
    borderColor: "#eee",
    padding: 10,
    fontSize: 16,
  },
  countryItem: {
    padding: 12,
    borderBottomWidth: 1,
    borderColor: "#f0f0f0",
  },
  card: {
    alignItems: "center",
    padding: 24
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 24,
  },
  feeLabel: {
    fontSize: 14,
    fontWeight: "400",
    lineHeight: 18,
    color: Colors.titleTextColor,
    fontFamily: 'Rubik'
  },
  oldPrice: {
    fontSize: 16,
    marginRight: 8,
  },
  newPrice: {
    fontSize: 16,
    fontWeight: "bold",
    color: "green",
  },
  button: {
    backgroundColor: Colors.titleTextColor,
    borderRadius: 8,
    paddingVertical: 14,
    paddingHorizontal: 16,
    alignItems: 'center',
  },
  buttonText: {
    color: Colors.buttonTextColor,
    fontWeight: "500",
    fontFamily: 'Rubik',
    fontSize: 16,
    lineHeight: 20
  },
  powered: {
    fontSize: 12,
    color: "gray",
  },
});


